<template>
  <Admin>
    <v-main class="wrapper">
      <v-snackbar v-model="snackbar.show" :color="snackbar.color" :timeout="3000" top right>
        {{ snackbar.message }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="snackbar.show = false">Close</v-btn>
        </template>
      </v-snackbar>

      <v-container fluid>
        <!-- Header -->
        <div class="d-flex align-center mb-4">
          <v-btn icon @click="goBack">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div class="ml-3">
            <h1 class="tw-text-xl tw-font-bold">{{ countSession.taskNumber }}</h1>
            <p class="text-subtitle-1 mb-0" v-if="countSession">
              {{ countSession.locationName }} - {{ formatDate(countSession.createdAt) }}
            </p>
          </div>
        </div>

        <v-divider class="mb-4" />

        <!-- Summary Cards -->
        <v-row v-if="countSession" class="mb-4">
          <v-col cols="12" md="3">
            <v-card>
              <v-card-text>
                <div class="text-subtitle-2 text--secondary">Total Assets</div>
                <div class="text-h4">{{ countSession.assetsTotal }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-card>
              <v-card-text>
                <div class="text-subtitle-2 text--secondary">Counted</div>
                <div class="text-h4 success--text">{{ countSession.assetsCount }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-card>
              <v-card-text>
                <div class="text-subtitle-2 text--secondary">Progress</div>
                <div class="text-h4">{{ Math.round(progressPercentage) === 100 ? '100' : progressPercentage.toFixed(1) }}%</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-card>
              <v-card-text>
                <div class="text-subtitle-2 text--secondary">Status</div>
                <v-chip :color="countSession.status === 'InProgress' ? 'warning' : 'success'">
                  {{ countSession.status }}
                </v-chip>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Filters and Assets Table in same container -->
        <v-card>
          <v-card-text>
            <!-- Filters -->
            <v-row class="mb-4">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="search"
                  label="Search"
                  prepend-icon="mdi-magnify"
                  dense
                  outlined
                  clearable
                  placeholder="Search by code, name..."
                  @input="applyFilters"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <v-select
                  v-model="filterStatus"
                  :items="statusOptions"
                  label="Count Status"
                  dense
                  outlined
                  clearable
                  @change="applyFilters"
                ></v-select>
              </v-col>
              <v-col cols="12" md="4">
                <!-- <v-chip v-if="selectedAssets.length > 0" color="primary" outlined class="mr-2">
                  <v-icon small left>mdi-checkbox-marked-circle</v-icon>
                  {{ selectedAssets.length }} รายการที่เลือก
                </v-chip> -->
                <v-btn
                  v-if="selectedAssets.length > 0 && countSession && countSession.status !== 'Complete'"
                  color="success"
                  small
                  height="40px"
                  class="tw-text-center "
                  @click="openCountDialog"
                >
                  <v-icon small left>mdi-check-circle</v-icon>
                  Change To Counted
                </v-btn>
              </v-col>
            </v-row>

            <!-- Assets Table -->
            <v-data-table
              :headers="tableHeaders"
              :items="paginatedAssets"
              :loading="loading"
              :items-per-page="itemsPerPage"
              :page.sync="currentPage"
              :sort-by="sortBy"
              :sort-desc="sortDesc"
              @update:sort-by="sortBy = $event"
              @update:sort-desc="sortDesc = $event"
              hide-default-footer
              class="elevation-0"
            >
              <template v-slot:header.select="{ }">
                <v-checkbox
                  v-model="selectAll"
                  :indeterminate="indeterminate"
                  hide-details
                  class="ma-0 pa-0"
                  @click="toggleSelectAll"
                ></v-checkbox>
              </template>

              <template v-slot:item.select="{ item }">
                <v-checkbox
                  v-model="selectedAssets"
                  :value="item"
                  :disabled="isSelectionDisabled(item)"
                  hide-details
                  class="ma-0 pa-0"
                ></v-checkbox>
              </template>

              <template v-slot:item.no="{ item, index }">
                {{ (currentPage - 1) * itemsPerPage + index + 1 }}
              </template>

              <template v-slot:item.countStatus="{ item }">
                <v-chip small :color="getCountStatusColor(item.countStatus)">
                  {{ item.countStatus || '-' }}
                </v-chip>
              </template>

              <template v-slot:item.countedAt="{ item }">
                {{ item.countedAt ? formatDate(item.countedAt) : '-' }}
              </template>

              <template v-slot:item.notes="{ item }">
                {{ item.notes || '-' }}
              </template>
            </v-data-table>

            <!-- Custom Pagination -->
            <v-row class="mt-4">
              <v-col cols="auto">
                <v-select
                  v-model="itemsPerPage"
                  :items="[10, 20, 30, 50, 100]"
                  dense
                  outlined
                  style="width: 90px"
                  @change="handleItemsPerPageChange"
                ></v-select>
              </v-col>
              <v-col>
                <v-pagination
                  v-model="currentPage"
                  :length="totalPages"
                  :total-visible="10"
                  @input="handlePageChange"
                ></v-pagination>
              </v-col>
              <v-col cols="auto" class="d-flex align-center">
                <span>จำนวนรายการทั้งหมด: {{ totalItems }}</span>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-container>
    </v-main>

    <!-- Count Dialog -->
    <v-dialog v-model="countDialog" max-width="800px" persistent scrollable>
      <v-card>
        <v-card-title>
          <span class="text-h6">Mark Assets as Counted</span>
        </v-card-title>
        <v-card-text style="max-height: 600px;">
          <v-container>
            <p class="mb-4">
              You are marking <strong>{{ selectedAssets.length }}</strong> asset(s) as counted.
            </p>
            <v-divider class="tw-mt-4 tw-mb-4"></v-divider>

            <!-- List each asset with individual notes input -->
            <div v-for="(asset, index) in selectedAssets" :key="asset.id" class="tw-mb-4">
              <v-row>
                <v-col cols="12">
                  <div class="tw-flex tw-items-center tw-mb-2">
                    <span class="font-weight-bold" style="min-width: 30px;">{{ index + 1 }}.</span>
                    <span class="font-weight-bold tw-mr-2">{{ asset.assetCode }}</span>
                    <span class="text--secondary">{{ asset.assetName }}</span>
                  </div>
                  <v-textarea
                    v-model="assetNotes[asset.id]"
                    
                    placeholder="Enter notes for this asset... (Optional)"
                    outlined
                    dense
                    rows="2"
                    hide-details
                  ></v-textarea>
                </v-col>
              </v-row>
              <v-divider v-if="index < selectedAssets.length - 1" class="mt-4"></v-divider>
            </div>
          </v-container>
        </v-card-text>
        <v-card-actions class="tw-p-4">
          <v-spacer></v-spacer>
          <v-btn text @click="closeCountDialog" class="tw-w-35 tw-mr-3">
            <v-icon left>mdi-close</v-icon>
            Cancel
          </v-btn>
          <v-btn style="background-color: #6ec39b; color: white;" @click="updateAssetsToCounted" :loading="updating">
            <v-icon left>mdi-check-circle</v-icon>
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </Admin>
</template>

<script>
import Admin from '@/components/admin/admin-main-template.vue'
import { mapState } from 'vuex'
import fc from "@/firebaseConfig"
import firebase from "firebase/app";

export default {
  name: 'FixedAssetsCountDetail',
  components: {
    Admin
  },
  data() {
    return {
      loading: false,
      countSession: null,
      assetsList: [],
      search: '',
      filterStatus: null,
      currentPage: 1,
      itemsPerPage: localStorage.getItem("pageLimit") === "auto" ? 10 : parseInt(localStorage.getItem("pageLimit")) || 20,
      sortBy: [],
      sortDesc: [],
      selectedAssets: [],
      countDialog: false,
      assetNotes: {},
      updating: false,
      itemsPerPageOptions: [10, 20, 30, 50, 100],
      snackbar: { show: false, message: '', color: '' },
      statusOptions: ['Counted', 'Uncounted'],
      headers: [
        { text: '', value: 'select', sortable: false, width: '50px' },
        { text: 'No.', value: 'no', sortable: false },
        { text: 'Asset Code', value: 'assetCode' },
        { text: 'Asset Name', value: 'assetName' },
        { text: 'Department', value: 'assetDepartment' },
        { text: 'Location', value: 'assetLocation' },
        // { text: 'Class', value: 'assetClass' },
        { text: 'Count Status', value: 'countStatus' },
        { text: 'Counted At', value: 'countedAt' },
        { text: 'Notes', value: 'notes' }
      ]
    }
  },
  computed: {
    tableHeaders() {
      // Hide checkbox column when status is Complete
      if (this.countSession && this.countSession.status === 'Complete') {
        return this.headers.filter(header => header.value !== 'select')
      }
      return this.headers
    },
    filteredAssets() {
      let filtered = [...this.assetsList]

      // Search filter
      if (this.search && this.search.trim() !== '') {
        const searchLower = this.search.toLowerCase().trim()
        filtered = filtered.filter(asset =>
          (asset.assetCode || '').toLowerCase().includes(searchLower) ||
          (asset.assetName || '').toLowerCase().includes(searchLower) ||
          (asset.assetDepartment || '').toLowerCase().includes(searchLower) ||
          (asset.assetLocation || '').toLowerCase().includes(searchLower) ||
          (asset.assetClass || '').toLowerCase().includes(searchLower)
        )
      }

      // Status filter
      if (this.filterStatus) {
        filtered = filtered.filter(asset => {
          const assetStatus = (asset.countStatus || 'Uncounted').toLowerCase()
          const filterStatusLower = this.filterStatus.toLowerCase()
          return assetStatus === filterStatusLower
        })
      }

      // Filter out Disposed assets when count session is InProgress
      if (this.countSession && this.countSession.status === 'InProgress') {
        filtered = filtered.filter(asset => {
          // Only hide assets that explicitly have status = 'Disposed'
          // Assets without a status field (undefined/null) will be shown
          return asset.status !== 'Disposed'
        })
      }

      return filtered
    },
    paginatedAssets() {
      let sorted = [...this.filteredAssets]
      
      // Apply sorting if sortBy is set
      if (this.sortBy.length > 0) {
        sorted.sort((a, b) => {
          for (let i = 0; i < this.sortBy.length; i++) {
            const sortKey = this.sortBy[i]
            const sortOrder = this.sortDesc[i] ? -1 : 1
            
            let aVal = a[sortKey]
            let bVal = b[sortKey]
            
            // Handle null/undefined values
            if (aVal == null) aVal = ''
            if (bVal == null) bVal = ''
            
            // Convert to string for comparison if needed
            if (typeof aVal !== 'string') aVal = String(aVal)
            if (typeof bVal !== 'string') bVal = String(bVal)
            
            const comparison = aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' })
            
            if (comparison !== 0) {
              return comparison * sortOrder
            }
          }
          return 0
        })
      }
      
      // Apply pagination
      const start = (this.currentPage - 1) * this.itemsPerPage
      const end = start + this.itemsPerPage
      return sorted.slice(start, end)
    },
    totalItems() {
      return this.filteredAssets.length
    },
    totalPages() {
      return Math.ceil(this.totalItems / this.itemsPerPage)
    },
    progressPercentage() {
      if (!this.countSession || !this.countSession.assetsTotal) return 0
      return (this.countSession.assetsCount / this.countSession.assetsTotal) * 100
    },
    selectableAssets() {
      // Only assets with 'Uncounted' status can be selected
      return this.paginatedAssets.filter(asset => {
        const status = (asset.countStatus || 'Uncounted').toLowerCase()
        return status === 'uncounted'
      })
    },
    selectAll() {
      return this.selectableAssets.length > 0 &&
             this.selectableAssets.every(asset => this.selectedAssets.includes(asset))
    },
    indeterminate() {
      const selectedCount = this.selectableAssets.filter(asset =>
        this.selectedAssets.includes(asset)
      ).length
      return selectedCount > 0 && selectedCount < this.selectableAssets.length
    }
  },
  watch: {
    sortBy() {
      this.currentPage = 1
    },
    sortDesc() {
      this.currentPage = 1
    }
  },
  async mounted() {
    await this.fetchData()
  },
  methods: {
    notify(type, message) {
      this.snackbar = { show: true, message, color: type }
    },
    async fetchData() {
      this.loading = true
      try {
        const companyId = this.$route.params.companyId
        const countId = this.$route.params.countId

        // Fetch count session data
        const countDoc = await fc.companies
          .doc(companyId)
          .collection('FixedAssetsCount')
          .doc(countId)
          .get()

        if (countDoc.exists) {
          this.countSession = {
            id: countDoc.id,
            ...countDoc.data()
          }
        }

        // Fetch assets list
        const response = await this.$store.dispatch('fixedAssets/fetchAssetsListByCountId', {
          companyId: companyId,
          countId: countId
        })

        if (response && response.success) {
          this.assetsList = response.assetsList || []
          console.log('Assets loaded:', this.assetsList.length)
          console.log('Sample asset:', this.assetsList[0])
        }

      } catch (error) {
        console.error('Error fetching assets count detail:', error)
        this.notify('error', 'Error loading assets count detail')
      } finally {
        this.loading = false
      }
    },
    formatDate(timestamp) {
      if (!timestamp) return ''
      if (timestamp && typeof timestamp.toDate === 'function') {
        return timestamp.toDate().toLocaleDateString('th-TH', {
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        })
      }
      const date = new Date(timestamp)
      return date.toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    },
    getCountStatusColor(status) {
      if (!status || status === 'Uncounted') return 'warning'
      if (status === 'Counted' || status === 'counted') return 'success'
      if (status === 'Missing' || status === 'missing') return 'error'
      return 'grey'
    },
    goBack() {
      this.$router.go(-1)
    },
    applyFilters() {
      // Reset to first page when filters change
      console.log('Applying filters - Status:', this.filterStatus, 'Search:', this.search)
      console.log('Filtered results:', this.filteredAssets.length)
      this.currentPage = 1
    },
    handleItemsPerPageChange() {
      this.currentPage = 1
    },
    handlePageChange() {
      // Pagination automatically handled by v-pagination
    },
    isSelectionDisabled(item) {
      // Only allow selection of items with 'Uncounted' status
      const status = (item.countStatus || 'Uncounted').toLowerCase()
      return status !== 'uncounted'
    },
    toggleSelectAll() {
      if (this.selectAll) {
        // Deselect all selectable items from current page
        this.selectedAssets = this.selectedAssets.filter(asset =>
          !this.selectableAssets.includes(asset)
        )
      } else {
        // Select all selectable items from current page
        const newSelections = this.selectableAssets.filter(asset =>
          !this.selectedAssets.includes(asset)
        )
        this.selectedAssets = [...this.selectedAssets, ...newSelections]
      }
    },
    openCountDialog() {
      this.countDialog = true
      this.assetNotes = {}
    },
    closeCountDialog() {
      this.countDialog = false
      this.assetNotes = {}
    },
    async updateAssetsToCounted() {
      if (this.selectedAssets.length === 0) return

      this.updating = true
      try {
        const companyId = this.$route.params.companyId
        const countId = this.$route.params.countId
        const batch = fc.db.batch()
        const timestamp = firebase.firestore.FieldValue.serverTimestamp()

        // Update each selected asset
        for (const asset of this.selectedAssets) {
          const assetRef = fc.companies
            .doc(companyId)
            .collection('FixedAssetsCount')
            .doc(countId)
            .collection('assetsList')
            .doc(asset.id)

          const assetNotes = this.assetNotes[asset.id] || ''

          batch.update(assetRef, {
            countStatus: 'Counted',
            countedAt: timestamp,
            lastCountDate: timestamp,
            notes: assetNotes
          })

          // Also update in the local list
          const index = this.assetsList.findIndex(a => a.id === asset.id)
          if (index !== -1) {
            this.assetsList[index].countStatus = 'Counted'
            this.assetsList[index].countedAt = new Date()
            this.assetsList[index].lastCountDate = new Date()
            this.assetsList[index].notes = assetNotes
          }
        }

        // Commit the batch update
        await batch.commit()

        // Update count session statistics
        const countedCount = this.assetsList.filter(a =>
          a.countStatus && a.countStatus.toLowerCase() === 'counted'
        ).length

        // Check if all assets are counted
        const allAssetsCounted = countedCount === this.assetsList.length

        const updateData = {
          assetsCount: countedCount,
          updatedAt: timestamp
        }

        // If all assets are counted, update status to Complete
        if (allAssetsCounted) {
          updateData.status = 'Complete'
        }

        await fc.companies
          .doc(companyId)
          .collection('FixedAssetsCount')
          .doc(countId)
          .update(updateData)

        // Update local count session
        if (this.countSession) {
          this.countSession.assetsCount = countedCount
          this.countSession.updatedAt = timestamp
          if (allAssetsCounted) {
            this.countSession.status = 'Complete'
          }
        }

        this.notify('success', `Successfully marked ${this.selectedAssets.length} asset(s) as counted`)

        // Clear selections and close dialog
        this.selectedAssets = []
        this.closeCountDialog()

      } catch (error) {
        console.error('Error updating assets:', error)
        this.notify('error', 'Error updating assets: ' + error.message)
      } finally {
        this.updating = false
      }
    }
  }
}
</script>

<style scoped>
/* .wrapper {
  background-color: #f5f5f5;
} */
</style>
